/**
 * Settings Commands
 *
 * Commands for opening settings sections and toggling dark mode.
 */

import {
  Settings, Plug, Database, Bookmark, Palette, Terminal,
  RefreshCw, Sparkles, Shield, Wrench, Moon, MousePointerClick, FileText,
} from 'lucide-react';
import type { CommandDefinition } from '../types/commandTypes';
import { useCommandPaletteStore } from '../store/commandPaletteStore';
import { useSettingsDialogStore } from '../../main-toolbar/store/settingsDialogStore';
import { useAppStore } from '@/store/appStore';

/** Close palette and run after a tick */
function closeAndRun(fn: () => void) {
  useCommandPaletteStore.getState().close();
  setTimeout(fn, 0);
}

/** Settings section config: id, label, icon, keywords */
const settingsSections = [
  { section: 'license' as const, label: 'Account', icon: Settings, keywords: ['license', 'account', 'subscription'] },
  { section: 'connections' as const, label: 'Connections', icon: Database, keywords: ['connection', 'server'] },
  { section: 'sessions' as const, label: 'Sessions', icon: Bookmark, keywords: ['session', 'save', 'restore'] },
  { section: 'snippets' as const, label: 'Snippets', icon: Bookmark, keywords: ['snippet', 'template', 'code'] },
  { section: 'ui' as const, label: 'UI', icon: Palette, keywords: ['theme', 'font', 'appearance', 'ui', 'dark mode', 'tab size', 'schema grouping', 'object explorer'] },
  { section: 'ai-terminal' as const, label: 'AI & Terminal', icon: Terminal, keywords: ['ai', 'terminal', 'chat'] },
  { section: 'ai-integrations' as const, label: 'AI Integrations', icon: Plug, keywords: ['mcp', 'copilot', 'integration'] },
  { section: 'updates' as const, label: 'Updates', icon: RefreshCw, keywords: ['update', 'version', 'auto-update'] },
  { section: 'whats-new' as const, label: "What's New", icon: Sparkles, keywords: ['changelog', 'release', 'new'] },
  { section: 'privacy' as const, label: 'Privacy', icon: Shield, keywords: ['privacy', 'analytics', 'telemetry'] },
  { section: 'advanced' as const, label: 'Advanced', icon: Wrench, keywords: ['advanced', 'debug', 'experimental'] },
] as const;

/** Generate one command per settings section */
const sectionCommands: CommandDefinition[] = settingsSections.map(({ section, label, icon, keywords }) => ({
  id: `settings:${section}`,
  label: `Settings: ${label}`,
  category: 'settings' as const,
  icon,
  keywords: ['settings', ...keywords],
  execute: () => closeAndRun(() => {
    useSettingsDialogStore.getState().openToSection(section);
  }),
}));

export const settingsCommands: CommandDefinition[] = [
  ...sectionCommands,
  {
    id: 'settings:toggle-dark-mode',
    label: 'Toggle Dark Mode',
    category: 'settings',
    icon: Moon,
    keywords: ['dark', 'light', 'theme', 'mode'],
    execute: () => {
      useCommandPaletteStore.getState().close();
      useAppStore.getState().toggleDarkMode();
    },
  },
  {
    id: 'settings:toggle-inline-execute-bar',
    label: 'Toggle Inline Execute / Peek Bar',
    category: 'settings',
    icon: MousePointerClick,
    keywords: [
      'peek', 'execute', 'cursor', 'selection', 'inline', 'floating', 'bar',
      'statement', 'run', 'quick', 'preview',
    ],
    execute: () => {
      useCommandPaletteStore.getState().close();
      const { editorSettings, setEditorSettings } = useAppStore.getState();
      setEditorSettings({
        showInlineExecuteBar: !editorSettings.showInlineExecuteBar,
      });
    },
  },
  {
    id: 'settings:open-logs',
    label: 'Open Logs Folder',
    category: 'settings',
    icon: FileText,
    keywords: ['log', 'logs', 'diagnostics', 'troubleshoot', 'support'],
    execute: () => closeAndRun(() => {
      window.electronAPI?.analytics?.track('command-palette:open_logs_folder').catch(() => {
        /* fire-and-forget */
      });
      void window.electronAPI?.openLogsFolder();
    }),
  },
];
